import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SubscriptionProvider } from "@/context/SubscriptionContext";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import PortfolioBuilder from "@/pages/PortfolioBuilder";
import Compare from "@/pages/Compare";
import AuthCallback from "@/pages/AuthCallback";
import NativeAuthReturn from "@/pages/NativeAuthReturn";
import Pricing from "@/pages/Pricing";
import ComparisonPage from "@/pages/ComparisonPage";
import CompareHub from "@/pages/CompareHub";
import CompareField from "@/pages/CompareField";
import Calculator from "@/pages/Calculator";
import AffiliateApply from "@/pages/AffiliateApply";
import AffiliateDashboard from "@/pages/AffiliateDashboard";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ComingSoon from "@/pages/ComingSoon";
import Admin from "@/pages/Admin";
import Research from "@/pages/Research";
import HowItWorks from "@/pages/HowItWorks";
import Holdings from "@/pages/Holdings";
import Conversations from "@/pages/Conversations";
import Trial from "@/pages/Trial";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import WhyDpp from "@/pages/WhyDpp";
import Install from "@/pages/Install";
import TrustCenter from "@/pages/TrustCenter";
import MarketRecap from "@/pages/MarketRecap";
import Faq from "@/pages/Faq";
import Demo from "@/pages/Demo";
import Layout from "@/components/Layout";
import NativeBridge from "@/components/NativeBridge";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();

  // Fire a first-party pageview beacon on every route change. Non-blocking,
  // never throws so the SPA render is unaffected. Uses `document.title`
  // captured after a short micro-tick so per-page usePageSeo() has run.
  useEffect(() => {
    if (location.hash?.includes("session_id=")) return; // don't log auth-handshake
    const path = location.pathname + (location.search || "");
    const referrer = document.referrer || null;
    const send = () => {
      const title = document.title || null;
      // Fire-and-forget — the beacon endpoint is public and always 200s.
      api.post("/analytics/pageview", { path, referrer, title }).catch(() => {});
    };
    // Wait one animation frame so usePageSeo has updated document.title.
    const raf = requestAnimationFrame(send);
    return () => cancelAnimationFrame(raf);
  }, [location.pathname, location.search]);

  // Affiliate attribution — ANY page landed on with `?aff=slug` is credited.
  // We stash in localStorage so it survives the auth/register redirect, and
  // fire the click tracker in the background. Sticky until the visitor
  // actually signs up (see AuthContext.register). UTM params passed alongside
  // so affiliates can see which post/video drove the traffic.
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const aff = params.get("aff");
    if (!aff) return;
    const clean = aff.trim().toLowerCase().slice(0, 40);
    if (!clean) return;
    localStorage.setItem("dp_affiliate_ref", clean);
    const utm = {};
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
      const v = params.get(k);
      if (v) utm[k] = v.slice(0, 100);
    }
    if (Object.keys(utm).length) localStorage.setItem("dp_affiliate_utm", JSON.stringify(utm));
    const q = new URLSearchParams({ slug: clean, ...utm }).toString();
    api.get(`/affiliate/track?${q}`).catch(() => {});
  }, [location.search]);

  if (location.pathname === "/auth/native-return") {
    return <NativeAuthReturn />;
  }
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/how-we-rank" element={<WhyDpp />} />
        {/* Legacy path — renders the same WhyDpp component so external
            backlinks (old newsletter emails, indexed Google results) still
            resolve to real content. `usePageSeo` inside WhyDpp emits a
            canonical to `/how-we-rank`, which tells Google to consolidate
            without triggering GSC's "Page with redirect" warning that a
            hard `<Navigate>` would produce. */}
        <Route path="/why-dpp" element={<WhyDpp />} />
        <Route path="/install" element={<Install />} />
        <Route path="/download" element={<Install />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/vs" element={<CompareHub />} />
        {/* Aggregate "DPP vs. the field" — the mega scoreboard combining
            every competitor into one table. Must sit BEFORE `/vs/:slug` so
            the router doesn't treat "field" as a slug lookup. */}
        <Route path="/vs/field" element={<CompareField />} />
        <Route path="/calculator" element={<Calculator />} />
        <Route path="/vs/:slug" element={<ComparisonPage />} />
        <Route path="/affiliate" element={<AffiliateApply />} />
        <Route path="/affiliate/dashboard/:token" element={<AffiliateDashboard />} />
        <Route path="/coming-soon" element={<ComingSoon />} />
        <Route path="/trust" element={<TrustCenter />} />
        <Route path="/market-recap" element={<MarketRecap />} />
        <Route path="/market-recap/:slug" element={<MarketRecap />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/demo" element={<Demo />} />
        <Route path="/admin" element={<Protected><Admin /></Protected>} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/portfolio/new" element={<PortfolioBuilder />} />
        <Route path="/portfolio/:id" element={<Protected><PortfolioBuilder /></Protected>} />
        <Route path="/compare" element={<Protected><Compare /></Protected>} />
        <Route path="/research" element={<Research />} />
        <Route path="/holdings" element={<Protected><Holdings /></Protected>} />
        <Route path="/conversations" element={<Protected><Conversations /></Protected>} />
        <Route path="/trial/:token" element={<Trial />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <BrowserRouter>
          <NativeBridge />
          <AppRoutes />
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
